'use client';

/**
 * OnboardingMilestone Component
 *
 * Single milestone item within an onboarding section. Displays an icon, title,
 * status indicator (locked/available/in_progress/completed/skipped), and a
 * "Let Astor help" button that triggers the milestone's astorAction.
 * Completed milestones show a checkmark with a glow animation.
 *
 * @see docs/plans/2026-02-20-DESIGN-astor-ai-native-onboarding-help.md
 */

import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import LockIcon from '@mui/icons-material/Lock';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import SkipNextIcon from '@mui/icons-material/SkipNext';
import { alpha, Box, Typography, useTheme } from '@mui/material';
import type { MilestoneStatus, OnboardingMilestone as OnboardingMilestoneType } from '@ops-matrix/shared-types';
import { AnimatePresence, motion } from 'framer-motion';
import React, { useCallback } from 'react';

import { useI18n } from '../../hooks/useI18n';
import { useReducedMotion } from '../../hooks/useReducedMotion';
import { glassEffects } from '../../theme/shadows';
import { iconSizes } from '../../theme/typography';

// ============================================================================
// TYPES
// ============================================================================

export interface OnboardingMilestoneProps {
  /** The milestone data */
  milestone: OnboardingMilestoneType;
  /** Whether this milestone has been completed (from completedMilestones Set) */
  isCompleted?: boolean;
  /** Callback when the "Let Astor help" button is clicked */
  onAstorAction: (milestoneId: string) => void;
  /** Whether this milestone is within a locked section */
  sectionLocked?: boolean;
}

// ============================================================================
// STATUS ICON MAP
// ============================================================================

const STATUS_ICONS: Record<MilestoneStatus, React.ElementType> = {
  locked: LockIcon,
  available: RadioButtonUncheckedIcon,
  in_progress: PlayArrowIcon,
  completed: CheckCircleIcon,
  skipped: SkipNextIcon,
};

const STATUS_COLORS: Record<MilestoneStatus, string> = {
  locked: 'text.disabled',
  available: 'primary.main',
  in_progress: 'info.main',
  completed: 'success.main',
  skipped: 'text.secondary',
};

// ============================================================================
// COMPONENT
// ============================================================================

export function OnboardingMilestone({
  milestone,
  isCompleted: isCompletedProp = false,
  onAstorAction,
  sectionLocked = false,
}: OnboardingMilestoneProps): React.JSX.Element {
  const theme = useTheme();
  const { t } = useI18n();
  const prefersReducedMotion = useReducedMotion();

  const isLocked = milestone.status === 'locked' || sectionLocked;
  const isCompleted = isCompletedProp || milestone.status === 'completed';
  const isSkipped = milestone.status === 'skipped';
  const isActionable = !isLocked && !isCompleted && !isSkipped;

  // Use completed icon when milestone is in completedMilestones set, even if plan JSON status is 'available'
  const effectiveStatus: MilestoneStatus = isCompleted ? 'completed' : milestone.status;
  const StatusIcon = STATUS_ICONS[effectiveStatus];

  const handleAstorAction = useCallback(() => {
    if (isActionable) {
      onAstorAction(milestone.id);
    }
  }, [isActionable, onAstorAction, milestone.id]);

  const completedGlowVariants = {
    idle: {
      boxShadow: `0 0 0px ${alpha(theme.palette.success.main, 0)}`,
    },
    glow: {
      boxShadow: [
        `0 0 8px ${alpha(theme.palette.success.main, 0.3)}`,
        `0 0 16px ${alpha(theme.palette.success.main, 0.15)}`,
        `0 0 8px ${alpha(theme.palette.success.main, 0)}`,
      ],
      transition: {
        duration: 2,
        repeat: 0,
        ease: 'easeInOut',
      },
    },
  };

  return (
    <Box
      component={prefersReducedMotion ? 'div' : motion.div}
      {...(!prefersReducedMotion &&
        isCompleted && {
          variants: completedGlowVariants,
          initial: 'idle',
          animate: 'glow',
        })}
      sx={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 1.5,
        px: 2,
        py: 1.5,
        borderRadius: 1.5,
        opacity: isLocked ? 0.5 : 1,
        bgcolor: isCompleted
          ? alpha(theme.palette.success.main, 0.06)
          : effectiveStatus === 'in_progress'
            ? alpha(theme.palette.info.main, 0.06)
            : 'transparent',
        transition: 'background-color 0.2s ease',
        '&:hover': !isLocked
          ? {
              bgcolor: alpha(theme.palette.primary.main, 0.06),
            }
          : undefined,
      }}
      data-testid={`onboarding-milestone-${milestone.id}`}
    >
      {/* Status Icon */}
      <Box
        sx={{
          mt: 0.25,
          color: STATUS_COLORS[effectiveStatus],
          display: 'flex',
          alignItems: 'center',
          flexShrink: 0,
        }}
      >
        <StatusIcon sx={{ fontSize: iconSizes.base }} />
      </Box>

      {/* Content */}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography
          variant="body2"
          sx={{
            fontWeight: isCompleted ? 500 : 600,
            color: isLocked ? 'text.disabled' : 'text.primary',
            textDecoration: isSkipped ? 'line-through' : 'none',
          }}
        >
          {milestone.title}
        </Typography>

        <Typography
          variant="caption"
          sx={{
            color: 'text.secondary',
            display: 'block',
            mt: 0.25,
          }}
        >
          {milestone.description}
        </Typography>

        {/* Estimated time */}
        {milestone.estimatedMinutes > 0 && (
          <Typography variant="caption" sx={{ color: 'text.disabled', mt: 0.5, display: 'block' }}>
            {t('astor.onboarding.estimatedTime', { minutes: milestone.estimatedMinutes })}
          </Typography>
        )}

        {/* Let Astor help button */}
        <AnimatePresence>
          {isActionable && (
            <Box
              component={prefersReducedMotion ? 'button' : motion.button}
              {...(!prefersReducedMotion && {
                initial: { opacity: 0, y: 4 },
                animate: { opacity: 1, y: 0 },
                exit: { opacity: 0, y: 4 },
                transition: { duration: 0.2 },
                whileHover: { scale: 1.02 },
                whileTap: { scale: 0.98 },
              })}
              onClick={handleAstorAction}
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 0.5,
                mt: 1,
                px: 1.5,
                minHeight: 44,
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 2,
                bgcolor: alpha(theme.palette.primary.main, 0.08),
                color: 'primary.main',
                cursor: 'pointer',
                fontSize: '0.75rem',
                fontWeight: 600,
                fontFamily: 'inherit',
                lineHeight: 1.5,
                transition: 'all 0.2s ease',
                '&:hover': {
                  bgcolor: alpha(theme.palette.primary.main, 0.14),
                  borderColor: 'primary.main',
                  boxShadow: glassEffects.light.shadow,
                },
              }}
              data-testid={`milestone-astor-action-${milestone.id}`}
            >
              <AutoAwesomeIcon sx={{ fontSize: iconSizes.xs }} />
              {t('astor.onboarding.letAstorHelp')}
            </Box>
          )}
        </AnimatePresence>
      </Box>
    </Box>
  );
}

OnboardingMilestone.displayName = 'OnboardingMilestone';

export default OnboardingMilestone;
